<template>
  <Transition name="offline-banner">
    <div
      v-if="message"
      class="offline-banner"
      :class="{ 'offline-banner--syncing': !isOffline }"
      role="status"
      aria-live="polite"
    >
      <svg v-if="isOffline" class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636a9 9 0 010 12.728m-12.728 0a9 9 0 010-12.728m2.829 9.9a5 5 0 010-7.072m7.07 0a5 5 0 010 7.072M3 3l18 18" />
      </svg>
      <svg v-else class="w-4 h-4 flex-shrink-0 offline-banner-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      <span>{{ message }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { pendingOutboxCount } from "../services/apiClient"

const { t } = useI18n()

const isOffline = ref(typeof navigator !== "undefined" && !navigator.onLine)

function onOnline() {
  isOffline.value = false
}
function onOffline() {
  isOffline.value = true
}

onMounted(() => {
  window.addEventListener("online", onOnline)
  window.addEventListener("offline", onOffline)
})
onUnmounted(() => {
  window.removeEventListener("online", onOnline)
  window.removeEventListener("offline", onOffline)
})

// Offline beats syncing: while disconnected we explain that changes are kept;
// once back online with a non-empty outbox we show the sync-in-progress state
// until the replay drains it.
const message = computed(() => {
  if (isOffline.value) {
    return pendingOutboxCount.value > 0
      ? t("offline.pending", { count: pendingOutboxCount.value })
      : t("offline.banner")
  }
  if (pendingOutboxCount.value > 0) {
    return t("offline.syncing", { count: pendingOutboxCount.value })
  }
  return ""
})
</script>

<style scoped>
.offline-banner {
  position: fixed;
  top: calc(var(--safe-top) + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-banner);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: calc(100vw - 2rem);
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  background: var(--ink-900, #12171a);
  color: var(--ink-50, #faf9f7);
  font-size: 0.8125rem;
  box-shadow: var(--shadow-lg);
  pointer-events: none;
}

.offline-banner--syncing {
  background: var(--accent, #17505f);
}

.offline-banner-spin {
  animation: offline-banner-rotate 1.2s linear infinite;
}

@keyframes offline-banner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.offline-banner-enter-active,
.offline-banner-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.offline-banner-enter-from,
.offline-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
</style>
